import React, { useState } from 'react';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import styled from '@emotion/styled';
import { useQuery } from 'apollo-client-ppc';
import { ValueType } from '@atlaskit/select';
import Button from '@atlaskit/button';
import { BillingProfilePanelV2 } from './billing-profile-panel-v2';
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle, ModalTransition } from '@atlaskit/modal-dialog';
import { CCPPricing } from '@atlassian/cssxp-typescommon';
import { BillingProfileCardsLoader } from '@css-xp/ppc-page-loaders';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { useFetchEccFromContactService } from '@css-xp/contact-resources';
import { token } from '@atlaskit/tokens';
import { N500 } from '@atlaskit/theme/colors';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import {
  FetchCustomerBillingProfilesForTxaInPpcQuery,
  FetchCustomerBillingProfilesForTxaInPpcQueryVariables,
  InvoiceGroup,
  Currency,
  ContactInfo,
} from '@css-xp/graphtypes/src/generated-types-new';
import { CCP_SEARCH_MAX_LIMIT, defaultBillingProfileSortOptions, maxThresholdForResults } from '@css-xp/ppc-constants';
import { sortBillingProfiles, isAchPaymentMethod } from '@css-xp/ppc-utils';
import { CartCustomerSearchItem } from '../../../customer/src/types';
import { fetchCustomerBillingProfilesForTxaInPPC } from '../../../order-summary/src/queries/fetch-billing-profiles-for-transaction-account';
import { ChangeBillingProfile } from '../../../order-summary/src/components/change-billing-profile';
import { BillingProfileOption } from '../../../order-summary/src/components/order-summary';

interface Props {
  onClose?: () => void;
  isModalOpen?: boolean;
  transactionAccountId: string;
  customer: CartCustomerSearchItem;
  onSubmit?: (_billingProfileId: string) => void;
  isPaypalEnabled?: boolean;
  openCreateBillingProfile?: () => void;
  showPrimaryContacts?: boolean;
  isFetchBillingProfilesViaCCPEnabled?: boolean;
  isNewBPCreationFlowEnabled?: boolean;
  version?: number | null;
  onBillingProfilesFetch?: (billingProfiles: InvoiceGroup[]) => void;
  shouldShowACHPaymentMethod?: boolean;
}

const SubmitButtonWrapper = styled.div(() => ({
  width: '85px',
  textAlign: 'right',
}));

const ModalFooterWrapper = styled.div(() => ({}));

const LoadingIndicatorWrapper = styled.div(() => ({
  position: 'relative',
  minHeight: '200px',
}));

export const DestinationBillingProfileWrapper = ({
  isModalOpen,
  onClose,
  transactionAccountId,
  customer,
  onSubmit,
  isPaypalEnabled,
  openCreateBillingProfile,
  showPrimaryContacts,
  isFetchBillingProfilesViaCCPEnabled,
  isNewBPCreationFlowEnabled,
  version,
  onBillingProfilesFetch,
  shouldShowACHPaymentMethod,
}: Props) => {
  const [selectedBillingProfile, setSelectedBillingProfile] = useState<ValueType<BillingProfileOption> | null>(null);
  const [allPrimaryContacts, setAllPrimaryContacts] = useState<ContactInfo[]>([]);
  const [allSecondaryContacts, setAllSecondaryContacts] = useState<ContactInfo[]>([]);
  const [isECCDetailsLoading, setIsECCDetailsLoading] = useState(true);
  const { getBulkECCDetails } = useFetchEccFromContactService();
  const isVersion2 = version === 2;

  const { data: billingProfilesForCustomer, loading } = useQuery<
    FetchCustomerBillingProfilesForTxaInPpcQuery,
    FetchCustomerBillingProfilesForTxaInPpcQueryVariables
  >(fetchCustomerBillingProfilesForTxaInPPC, {
    variables: {
      id: transactionAccountId,
      filterOptions: [
        {
          key: 'or',
          // sending multiple ship-to ids
          value: JSON.stringify(customer?.ids?.map((shipToId) => ({ shipToId }))),
        },
      ],
      searchOptions: {
        first: isFetchBillingProfilesViaCCPEnabled ? CCP_SEARCH_MAX_LIMIT : maxThresholdForResults,
        offset: 0,
        sort: defaultBillingProfileSortOptions,
      },
      isFetchBillingProfilesViaCCPEnabled,
    },
    notifyOnNetworkStatusChange: true,
    nextFetchPolicy: 'cache-and-network',
    skip: !transactionAccountId || !customer?.ids || !customer?.ids.length,
  });

  const billingProfilesForCustomerData = isFetchBillingProfilesViaCCPEnabled
    ? get(billingProfilesForCustomer, 'transactionAccount.billingProfilesFromCCP.nodes', [])
    : get(billingProfilesForCustomer, 'transactionAccount.billingProfiles.nodes', []);

  let filteredBillingProfiles = billingProfilesForCustomerData.map((billingProfile: InvoiceGroup) => {
    return {
      ...billingProfile,
      shipToPriceEligibility: billingProfile?.shipToPriceEligibility?.toUpperCase() ?? CCPPricing.COMMERCIAL,
      currency: billingProfile?.currency?.toUpperCase() ?? Currency.USD,
    };
  }) as InvoiceGroup[];
  /* we're using this sorting because the response from api is ASCII sorted, so we need to sort it in a case-insensitive way.
   * This is a temporary solution, and we are only using it here since we are fetching all the billing profiles in one go.
   */

  filteredBillingProfiles = sortBillingProfiles(filteredBillingProfiles, true, true);

  filteredBillingProfiles = filteredBillingProfiles?.filter((billingProfile) => {
    if (!shouldShowACHPaymentMethod && isAchPaymentMethod(billingProfile?.defaultPaymentMethod?.type)) {
      return false;
    }

    return billingProfile?.isActive === true;
  });

  React.useEffect(() => {
    (async () => {
      if (showPrimaryContacts && !loading && !!filteredBillingProfiles && filteredBillingProfiles?.length > 0) {
        const invoiceGroupIds = filteredBillingProfiles?.map((billingProfile: any) => billingProfile?.id);
        const eccDetails = await getBulkECCDetails(invoiceGroupIds as string[], transactionAccountId);

        if (!isEmpty(eccDetails)) {
          const primaryContacts = eccDetails?.filter((contact: ContactInfo) => contact?.isPrimary);
          setAllPrimaryContacts(primaryContacts);

          const secondaryContacts = eccDetails?.filter((contact: ContactInfo) => !contact?.isPrimary);
          setAllSecondaryContacts(secondaryContacts);
        }
        setIsECCDetailsLoading(false);
      } else if (!showPrimaryContacts || filteredBillingProfiles?.length === 0) {
        setIsECCDetailsLoading(false);
      }
      if (!loading && filteredBillingProfiles?.length > 0 && isVersion2 && onBillingProfilesFetch) {
        onBillingProfilesFetch(filteredBillingProfiles);
      }
    })();
  }, [loading]);

  const handleSubmitButton = () => {
    if (selectedBillingProfile && onSubmit) {
      onSubmit(selectedBillingProfile.value);
      setSelectedBillingProfile(null);
    }
  };

  const renderBillingProfilesSection = () => {
    return loading || isECCDetailsLoading ? (
      <BillingProfileCardsLoader />
    ) : (
      <BillingProfilePanelV2
        billingProfiles={filteredBillingProfiles}
        primaryContacts={allPrimaryContacts}
        isPaypalEnabled={!!isPaypalEnabled}
        onSelectBillingProfile={onSubmit}
        openCreateBillingProfile={openCreateBillingProfile}
        shouldShowACHPaymentMethod={shouldShowACHPaymentMethod}
      />
    );
  };

  return isVersion2 ? (
    renderBillingProfilesSection()
  ) : (
    <ModalTransition>
      {isModalOpen && (
        <Modal
          onClose={onClose}
          testId="destination-billing-profile-modal"
          shouldCloseOnOverlayClick={false}
          shouldScrollInViewport
        >
          <ModalHeader>
            <ModalTitle>Select billing profile</ModalTitle>
            <Button appearance="link" onClick={onClose}>
              <CrossIcon size="small" label="Close Modal" primaryColor={token('color.text.subtle', N500)} />
            </Button>
          </ModalHeader>
          <ModalBody>
            <div>
              {loading || isECCDetailsLoading ? (
                <LoadingIndicatorWrapper>
                  <LoadingIndicator />
                </LoadingIndicatorWrapper>
              ) : (
                <ChangeBillingProfile
                  billingProfilesInfo={filteredBillingProfiles}
                  selectedBillingProfile={selectedBillingProfile}
                  setSelectedBillingProfile={(selectedBillingProfileForEstimate) => {
                    if (selectedBillingProfileForEstimate) {
                      setSelectedBillingProfile(selectedBillingProfileForEstimate);
                    }
                  }}
                  isDeferredPaymentMethodDisabled={true}
                  isNoPaymentMethodDisabled={false}
                  showPricingEligibility={true}
                  isPaypalEnabled={isPaypalEnabled}
                  openCreateBillingProfile={openCreateBillingProfile}
                  primaryContacts={allPrimaryContacts}
                  secondaryCustomerContacts={allSecondaryContacts}
                  showPrimaryContacts={showPrimaryContacts}
                  shouldAllowAddingContacts={false}
                  isNewBPCreationFlowEnabled={isNewBPCreationFlowEnabled}
                />
              )}
            </div>
          </ModalBody>
          <ModalFooter>
            <ModalFooterWrapper>
              <SubmitButtonWrapper>
                <Button appearance="primary" onClick={handleSubmitButton} isDisabled={!selectedBillingProfile}>
                  Submit
                </Button>
              </SubmitButtonWrapper>
            </ModalFooterWrapper>
          </ModalFooter>
        </Modal>
      )}
    </ModalTransition>
  );
};
